/* ============================================================
 * DeviceApiService (async/await, mejorado)
 * ------------------------------------------------------------
 * Servicio para registrar, consultar y actualizar dispositivos
 * en la API del backend, incluyendo X-User-Login para auditoría.
 * ============================================================ */

import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment.prod';
import { Storage } from '@ionic/storage-angular';
import { DeviceInfoModel } from '../device-core.service';
import { AuthService } from 'src/app/core/services/auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class DeviceApiService {
  private API_URL = environment.url_web_services_path + 'device_api/device/';
  private currentDeviceSubject = new BehaviorSubject<DeviceInfoModel | null>(null);

  constructor(
    private http: HttpClient,
    private storage: Storage,
    private authService: AuthService
  ) {
    this.initStorage();
  }

  // 🔹 Inicializa storage
  private async initStorage() {
    await this.storage.create();
    const savedDevice = await this.storage.get('currentDevice');
    if (savedDevice) {
      this.currentDeviceSubject.next(savedDevice);
      console.log('[DeviceApi] Cargado desde Storage:', savedDevice);
    }
  }

  // 🔹 Construir headers con X-User-Login
  private async getHeaders(): Promise<HttpHeaders> {
    const user = await this.authService.getCurrentUserAsync();
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    if (user?.login) {
      headers = headers.set('X-User-Login', user.login);
    }
    return headers;
  }

  /* ============================================================
   * 🔹 Registrar o actualizar dispositivo (mejorado)
   * ============================================================ */
  async registerOrUpdateDevice(deviceInfo: DeviceInfoModel): Promise<DeviceInfoModel> {
    try {
      const headers = await this.getHeaders();
      const existingDevice = await this.getDeviceByToken(deviceInfo.deviceToken, headers);

      let resp: DeviceInfoModel;
      if (existingDevice?.id) {
        // 🔹 Actualizar dispositivo
        resp = await this.http
          .put<DeviceInfoModel>(`${this.API_URL}${existingDevice.id}/`, deviceInfo, { headers })
          .toPromise();
        console.log('[DeviceApi] Dispositivo actualizado:', resp);
      } else {
        // 🔹 Crear nuevo dispositivo
        resp = await this.http
          .post<DeviceInfoModel>(this.API_URL, deviceInfo, { headers })
          .toPromise();
        console.log('[DeviceApi] Dispositivo registrado:', resp);
      }

      this.setCurrentDevice(resp);
      return resp;

    } catch (err: any) {
      console.error('[DeviceApi] Error registrando/actualizando dispositivo:', err);
      if (err?.error) console.error('[DeviceApi] Error backend detalle:', JSON.stringify(err.error));
      throw err;
    }
  }

  /* ============================================================
   * 🔹 Consultar dispositivo por rowid
   * ============================================================ */
  async getDevice(rowid: number): Promise<DeviceInfoModel | null> {
    try {
      const headers = await this.getHeaders();
      const resp = await this.http.get<DeviceInfoModel>(`${this.API_URL}${rowid}/`, { headers }).toPromise();
      console.log('[DeviceApi] Info obtenida:', resp);
      return resp;
    } catch (err: any) {
      console.error('[DeviceApi] Error al obtener dispositivo:', err);
      if (err?.error) console.error('[DeviceApi] Error backend:', err.error);
      return null;
    }
  }

  /* ============================================================
   * 🔹 Consultar dispositivo por deviceToken
   * ============================================================ */
  private async getDeviceByToken(deviceToken: string, headers: HttpHeaders): Promise<any> {
    if (!deviceToken) return null;
    try {
      const resp: any = await this.http.get(`${this.API_URL}?deviceToken=${deviceToken}`, { headers }).toPromise();
      if (resp?.data?.length) {
        console.log('[DeviceApi] Búsqueda por token:', resp.data[0]);
        return resp.data[0];
      }
      return null;
    } catch (err: any) {
      console.error('[DeviceApi] Error buscando dispositivo por token:', err);
      if (err?.error) console.error('[DeviceApi] Error backend:', err.error);
      return null;
    }
  }

  /* ============================================================
   * 🔹 Actualizar status del dispositivo (0=desactivado, 1=activo)
   * ============================================================ */
  async updateDeviceStatus(rowid: number, status: number): Promise<any> {
    try {
      const headers = await this.getHeaders();
      const resp = await this.http.patch(`${this.API_URL}${rowid}/`, { status }, { headers }).toPromise();
      console.log('[DeviceApi] Status actualizado:', resp);
      return resp;
    } catch (err: any) {
      console.error('[DeviceApi] Error al actualizar status:', err);
      if (err?.error) console.error('[DeviceApi] Error backend:', err.error);
      return null;
    }
  }

  /* ============================================================
   * 🔹 Dispositivo actual como observable
   * ============================================================ */
  get currentDevice$() {
    return this.currentDeviceSubject.asObservable();
  }

  /* ============================================================
   * 🔹 Obtener dispositivo actual async
   * ============================================================ */
  async getCurrentDeviceAsync(): Promise<DeviceInfoModel | null> {
    if (this.currentDeviceSubject.value) return this.currentDeviceSubject.value;
    await this.initStorage();
    return this.currentDeviceSubject.value;
  }

  /* ============================================================
   * 🔹 Actualizar BehaviorSubject y Storage
   * ============================================================ */
  setCurrentDevice(device: DeviceInfoModel | null) {
    this.currentDeviceSubject.next(device);
    if (device) this.storage.set('currentDevice', device);
    else this.storage.remove('currentDevice');
  }

  /* ============================================================
   * 🔹 Limpiar dispositivo del storage
   * ============================================================ */
  async clearDevice(): Promise<void> {
    this.currentDeviceSubject.next(null);
    await this.storage.remove('currentDevice');
    console.log('[DeviceApi] Dispositivo eliminado del almacenamiento local.');
  }
}
